import React, { useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSearchLocation } from '../contexts/LocationContext';
import { collection, getDocs, query, orderBy, where, limit, doc, getDoc, startAt, endAt } from 'firebase/firestore';
import { db, BUSINESSES } from '../firebase';
import RestaurantCard from '../components/RestaurantCard';
import CuisineFilter from '../components/CuisineFilter';
import Pagination from '../components/Pagination';
import ChunkErrorBoundary from '../components/ChunkErrorBoundary';
import site from '../content/site';
import { DISCOUNTS_ENABLED } from '../content/features';
import { searchRestaurants, haversine, coordinatesFor } from '../utils/search';
import { cuisineFacets, matchesCuisineFilter } from '../data/cuisines';
import {
  nearbyBounds, mergeById, withinRadius, chooseRadius,
  NEARBY_RADII_M, NEARBY_TARGET_COUNT, NEARBY_PER_RANGE_LIMIT,
} from '../utils/nearby';
import { normalizeCardSize, DEFAULT_CARD_SIZE } from '../data/cardSize';
import './HomePage.css';

// Lazily imported: maplibre-gl is around 240 KB and only a fraction of
// visitors open the map, so it must not sit in the bundle every homepage
// visitor downloads. HomePage itself stays eagerly imported for prerendering.
const RestaurantMap = lazy(() => import('../components/RestaurantMap'));

// Listings are paged rather than rendered all at once: the full active set
// used to mount in one grid, which is fine at a dozen restaurants and a slow
// first paint at several hundred.
const PAGE_SIZE = 8;

// Hard cap on how many listings are ever fetched in one go.
//
// This is a safety net, not paging. Real server-side paging is blocked on a
// design decision rather than on effort: distance sorting, the cuisine chip
// counts and the search box all work across the whole set, and none of them
// can see a page that hasn't been fetched. Fetching eight at a time would mean
// "nearest first" only sorted within the current page — worse than the cost it
// saves. Doing it properly needs geohash-based queries so the database can
// return the nearest N directly, plus moving cuisine filtering into the query.
//
// Until then this stops the page growing without bound: at the cap the newest
// listings past it simply aren't shown, which is a visible problem worth
// hitting rather than a page that quietly takes ten seconds to load.
const MAX_LISTINGS = 250;

// Short enough to read in full on a 320px screen. The previous wording
// ("Search restaurants, cuisine, city…") was clipped mid-word on small phones,
// which is the one place a placeholder actually has to do its job.
const SEARCH_PLACEHOLDER_MOBILE = 'Search bars, nightclubs, city…';
const SEARCH_PLACEHOLDER_DESKTOP = 'Search bars, nightclubs, city…';

const MOBILE_QUERY = '(max-width: 600px)';
// Below this the map and the list have to take turns, because a list column
// narrow enough to leave room for a usable map isn't a list any more. Kept in
// step with the .home-split breakpoint in HomePage.css.
const SPLIT_QUERY = '(min-width: 1080px)';

export default function HomePage() {
  const [restaurants, setRestaurants] = useState([]);
  // Restaurants fetched by proximity to the visitor's chosen location. Held
  // separately from `restaurants` and merged at filter time rather than folded
  // into it, so that clearing the location cleanly returns the default view.
  const [nearbyRestaurants, setNearbyRestaurants] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCuisines, setSelectedCuisines] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [view, setView] = useState('list'); // list | map
  const [cardSize, setCardSize] = useState(DEFAULT_CARD_SIZE);
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  // Set by the pager so the scroll effect only fires on a real page turn, not
  // when the page resets to 1 because the filters changed.
  const scrollAfterPaging = useRef(false);
  // Pins show the discount this viewer actually gets, same rule the
  // redemption uses — see pinDiscount in RestaurantMap.
  const { isBlack } = useAuth();
  const [mapDiscountTier, setMapDiscountTier] = useState(isBlack ? 'black' : 'free');

  // The active search location. Owned by LocationContext because the control
  // that sets it now lives in the navbar rather than on this page.
  const { location } = useSearchLocation();
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  // Wide enough to read a list and a map at the same time without either
  // becoming a column of scraps.
  const [isSplit, setIsSplit] = useState(() => window.matchMedia(SPLIT_QUERY).matches);

  useEffect(() => {
    const watch = (query, set) => {
      const mq = window.matchMedia(query);
      const sync = () => set(mq.matches);
      // Re-read before subscribing. The initial state was taken during the
      // first render, and a width change between then and now — a rotation, a
      // restored window, a resized preview pane — fires no event we are
      // listening for yet, leaving the layout stuck on the wrong one.
      sync();
      mq.addEventListener('change', sync);
      // Plain resize as well as the query's own event. The two are redundant
      // on a healthy browser and cheap, and the failure they guard against is
      // not cosmetic: a stale answer here decides whether the map is rendered
      // at all, so a missed event is a missing map rather than a wrong margin.
      window.addEventListener('resize', sync);
      return () => {
        mq.removeEventListener('change', sync);
        window.removeEventListener('resize', sync);
      };
    };
    const stopMobile = watch(MOBILE_QUERY, setIsMobile);
    const stopSplit = watch(SPLIT_QUERY, setIsSplit);
    return () => { stopMobile(); stopSplit(); };
  }, []);

  useEffect(() => {
    async function fetchRestaurants() {
      try {
        const q = query(
          collection(db, BUSINESSES),
          where('status', '==', 'active'),
          orderBy('sortOrder'),
          limit(MAX_LISTINGS)
        );
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setRestaurants(list);
        setFiltered(list);
      } catch {
        setRestaurants([]);
        setFiltered([]);
      } finally {
        setLoading(false);
      }
    }
    fetchRestaurants();
  }, []);

  // Proximity fetch, additive to the default listings above.
  //
  // The default query is capped at MAX_LISTINGS and ordered by sortOrder, which
  // is the right default view but the wrong set to answer "what is near me"
  // from: once the catalogue outgrows that cap, a visitor's closest restaurants
  // can simply be absent from it, and sorting what did load by distance ranks a
  // slice that never contained them. Nothing looks wrong when that happens,
  // which is what makes it worth fixing before it starts.
  //
  // Additive on purpose. The default set stays loaded, so free-text search
  // still reaches restaurants far from the visitor rather than quietly
  // narrowing to whatever is nearby.
  useEffect(() => {
    if (!location?.coords) {
      setNearbyRestaurants([]);
      return undefined;
    }

    // Guards against a slow response for a location the visitor has already
    // moved on from overwriting the results for their current one.
    let current = true;

    (async () => {
      const counts = [];
      let found = [];
      for (const radiusM of NEARBY_RADII_M) {
        const bounds = nearbyBounds(location.coords, radiusM);
        let docs;
        try {
          // One query per geohash range, in parallel — a handful at most.
          const snaps = await Promise.all(bounds.map(([start, end]) => getDocs(query(
            collection(db, BUSINESSES),
            where('status', '==', 'active'),
            orderBy('geohash'),
            startAt(start),
            endAt(end),
            limit(NEARBY_PER_RANGE_LIMIT)
          ))));
          docs = snaps.flatMap(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch {
          // Most likely the composite index is not deployed yet. The default
          // listings are already on screen and still sort by distance, so
          // failing quietly here degrades to the previous behaviour rather
          // than emptying the page.
          return;
        }
        if (!current) return;

        // The geohash ranges are a rectangular over-approximation of a circle,
        // so this drops the corners that fall outside the radius asked for.
        found = withinRadius(mergeById(docs), location.coords, radiusM, coordinatesFor);
        counts.push({ radiusM, count: found.length });
        if (found.length >= NEARBY_TARGET_COUNT) break;
      }

      if (!current) return;
      // chooseRadius is only consulted for the log-worthy case of never
      // reaching the target; `found` already holds the last radius tried.
      chooseRadius(counts);
      setNearbyRestaurants(found);
    })();

    return () => { current = false; };
  }, [location]);

  // Card size is an admin preference stored in Firestore (public read). Read
  // separately from the listings so a failure here only falls back to the
  // default appearance rather than emptying the page.
  useEffect(() => {
    getDoc(doc(db, 'config', 'appearance'))
      .then(snap => {
        if (snap.exists()) setCardSize(normalizeCardSize(snap.data().cardSize));
      })
      .catch(() => { /* keep the default */ });
  }, []);

  // Cuisine chips are built from what's actually listed, so they never offer a
  // filter that matches nothing. Derived from the unfiltered set on purpose —
  // counts that shrank as you selected would make the rail jump around.
  const facets = useMemo(
    () => cuisineFacets(location ? mergeById(nearbyRestaurants, restaurants) : restaurants),
    [restaurants, nearbyRestaurants, location]
  );

  useEffect(() => {
    // The proximity results are unioned in rather than replacing the default
    // set, so searching still reaches the whole loaded catalogue while the
    // distance sort gets the restaurants actually closest to the visitor.
    // mergeById keeps one copy of anything present in both.
    const pool = location ? mergeById(nearbyRestaurants, restaurants) : restaurants;

    let list = searchRestaurants(pool, search);
    list = list.filter(r => matchesCuisineFilter(r, selectedCuisines));

    if (location) {
      // Sort every located restaurant by real distance, closest first — no
      // radius cap, so the nearest options always surface.
      list = list
        .map(r => ({ restaurant: r, coords: coordinatesFor(r) }))
        .filter(({ coords }) => coords)
        .map(({ restaurant, coords }) => ({ ...restaurant, dist: haversine(location.coords, coords) }))
        .sort((a, b) => a.dist - b.dist);
    }

    // Featured listings are pinned first regardless of distance/sortOrder —
    // a stable sort, so it only moves featured items to the front without
    // disturbing the distance/sortOrder ordering otherwise.
    list = [...list].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

    setFiltered(list);
    // Any change to what's being shown puts the reader back on page one —
    // staying on page 4 of a result set that now has two pages would show an
    // empty list.
    setPage(1);
  }, [restaurants, nearbyRestaurants, search, location, selectedCuisines]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Return to the top of the results when the reader turns a page.
  //
  // In an effect rather than in the click handler, because the handler runs
  // before the new page exists: the old scroll fired against the outgoing
  // list, and if the incoming page was shorter the document shrank underneath
  // the animation and it stopped somewhere arbitrary — which is why it only
  // "sometimes" worked on a phone.
  //
  // The offset is measured from the real navbar rather than assumed. The
  // navbar is sticky, so aligning the results with the top of the viewport
  // parked the first restaurant underneath it — the "slightly cuts off the
  // top restaurant" on desktop.
  useEffect(() => {
    if (!scrollAfterPaging.current) return;
    scrollAfterPaging.current = false;

    const results = document.querySelector('.home-listings__results');
    if (!results) return;
    const navHeight = document.querySelector('.navbar')?.getBoundingClientRect().height || 0;
    const top = window.scrollY + results.getBoundingClientRect().top - navHeight - 12;

    // Instant, not smooth: turning a page is a jump, and a long smooth scroll
    // from the bottom of one page is the thing most easily interrupted by a
    // stray touch mid-flight.
    window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
  }, [page]);

  // Switching to the map on a phone brings the map to the top of the screen.
  //
  // The map is sticky and sized to reach the bottom edge, but it still begins
  // below the search box and the cuisine rail — so without this the reader
  // taps "Map" and is left looking at the list, with the thing they asked for
  // somewhere further down. Scrolling it up is what makes the switch feel like
  // a change of view rather than a change of page content.
  useEffect(() => {
    if (isSplit || view !== 'map') return;

    // The map arrives a beat after the switch — it is a lazy chunk behind a
    // Suspense boundary — so this waits for it rather than measuring the
    // fallback and scrolling to the wrong place. Bounded, so a map that never
    // loads doesn't leave a timer running.
    let tries = 0;
    const id = setInterval(() => {
      const map = document.querySelector('.restaurant-map');
      if (!map) {
        if (++tries > 40) clearInterval(id);
        return;
      }
      clearInterval(id);
      const navHeight = document.querySelector('.navbar')?.getBoundingClientRect().height || 0;
      const top = window.scrollY + map.getBoundingClientRect().top - navHeight - 12;
      // Instant: this is a snap to a new view, and it is the behaviour the
      // pager already uses. A smooth scroll here would also be at the mercy of
      // a stray touch part-way through.
      window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    }, 50);
    return () => clearInterval(id);
  }, [view, isSplit]);

  // Built once and placed either side by side or one at a time, so the two
  // layouts can't drift apart as either one is changed.
  const listBlock = (
    <>
      <div className={`home-listings__list home-listings__list--${cardSize}`}>
        {visible.map(r => (
          <RestaurantCard key={r.id} restaurant={r} size={cardSize} />
        ))}
      </div>
      <Pagination page={page} pages={pageCount} onChange={handlePageChange} />
    </>
  );

  const mapBlock = (
    <>
      {DISCOUNTS_ENABLED && <div className="home-map-discount-switch">
        <span>Show discounts for</span>
        <div role="group" aria-label="Discount tier">
          <button type="button" className={mapDiscountTier === 'free' ? 'active' : ''} aria-pressed={mapDiscountTier === 'free'} onClick={() => setMapDiscountTier('free')}>Volcyy</button>
          <button type="button" className={mapDiscountTier === 'black' ? 'active black' : ''} aria-pressed={mapDiscountTier === 'black'} onClick={() => setMapDiscountTier('black')}>Volcyy Black</button>
        </div>
      </div>}
      <ChunkErrorBoundary message="The map didn't load. You can keep browsing the list.">
        <Suspense
          fallback={
            <div className="home-listings__map-loading">
              <div className="spinner" aria-label="Loading map…" />
            </div>
          }
        >
          {/* Every match, not just this page: a map showing eight of fifty
              pins would misrepresent what's nearby. */}
          <RestaurantMap
            restaurants={filtered}
            location={location}
            isBlack={mapDiscountTier === 'black'}
            onSelect={r => navigate(`/business/${r.id}`)}
          />
        </Suspense>
      </ChunkErrorBoundary>
    </>
  );

  function handleToggleCuisine(label) {
    setSelectedCuisines(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  }

  function handleClearCuisines() {
    setSelectedCuisines(new Set());
  }

  function handlePageChange(next) {
    // The scroll itself happens in the effect below, once the new page has
    // actually rendered.
    scrollAfterPaging.current = true;
    setPage(next);
  }

  return (
    <main className="home-page">
      <section className="home-listings">
        <div className="home-listings__inner">
          {/* The page still needs its one h1, and it is still the sentence the
              prerendered HTML serves a crawler — but at the weight of a page
              heading rather than as a full-bleed banner between the reader and
              the restaurants they came for. */}
          <header className="home-listings__head">
            <h1 className="home-listings__title">{site.ROUTE_META['/'].h1}</h1>
            <p className="home-listings__intro">{site.ROUTE_META['/'].intro}</p>
          </header>

          <div className="home-listings__controls">
            <div className="home-listings__search-wrap">
              <svg className="home-listings__search-icon" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <circle cx="7.5" cy="7.5" r="5.5" stroke="#999" strokeWidth="1.5"/>
                <path d="M11.5 11.5L16 16" stroke="#999" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <input
                ref={searchInputRef}
                className="home-listings__search"
                type="search"
                placeholder={isMobile ? SEARCH_PLACEHOLDER_MOBILE : SEARCH_PLACEHOLDER_DESKTOP}
                value={search}
                onChange={e => setSearch(e.target.value)}
                aria-label="Search venues"
              />
            </div>

            {/* The location control is not here: it lives in the navbar, on
                this route only. See Navbar.jsx.

                No view switch on a wide screen either — both views are on
                screen at once there, so a control for choosing between them
                would have nothing to do. */}
            {!isSplit && !loading && filtered.length > 0 && (
              <div className="home-view-switch" role="group" aria-label="Results view">
                <button type="button" className={view === 'list' ? 'active' : ''} aria-pressed={view === 'list'} onClick={() => setView('list')}>List</button>
                <button type="button" className={view === 'map' ? 'active' : ''} aria-pressed={view === 'map'} onClick={() => setView('map')}>Map</button>
              </div>
            )}
          </div>

          {!loading && (
            <CuisineFilter
              facets={facets}
              selected={selectedCuisines}
              onToggle={handleToggleCuisine}
              onClear={handleClearCuisines}
              total={restaurants.length}
            />
          )}

          <div className="home-listings__results">
            {loading ? (
              <div className="home-listings__list" aria-live="polite">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="restaurant-card restaurant-card--skeleton" aria-hidden="true">
                    <div className="restaurant-card__thumb" />
                    <div className="restaurant-card__body">
                      <div className="skeleton-line" style={{ width: '58%', height: 16, marginBottom: 8 }} />
                      <div className="skeleton-line" style={{ width: '38%', height: 12, marginBottom: 7 }} />
                      <div className="skeleton-line" style={{ width: '24%', height: 11 }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="home-listings__empty">
                <p className="home-listings__empty-title">
                  {search
                    ? `No venues match "${search}".`
                    : selectedCuisines.size > 0
                    ? 'No venues match those filters.'
                    : location
                    ? `No venues found near ${location.label}.`
                    : 'No venues listed yet. Check back soon!'}
                </p>
                {selectedCuisines.size > 0 && (
                  <button
                    type="button"
                    className="home-listings__empty-btn"
                    onClick={handleClearCuisines}
                  >Clear filters</button>
                )}
              </div>
            ) : (
              <>
                <p className="home-listings__count" aria-live="polite">
                  <strong>{filtered.length}</strong>
                  {filtered.length === 1 ? ' venue' : ' venues'}
                  {location && (
                    <span className="home-listings__sort">
                      {' '}near {location.label}{isSplit || view === 'list' ? ' · nearest first' : ''}
                    </span>
                  )}
                </p>

                {/* Wide screens show both at once, so the toggle only decides
                    anything on a phone or tablet, where there is only room for
                    one of them. */}
                {isSplit ? (
                  <div className="home-split">
                    <div className="home-split__list">
                      {listBlock}
                    </div>
                    <aside className="home-split__map" aria-label="Map of these venues">
                      {mapBlock}
                    </aside>
                  </div>
                ) : view === 'list' ? (
                  listBlock
                ) : (
                  mapBlock
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Floating rather than inline: it stays reachable at any scroll depth,
          which is the whole point when the list runs to eight rows a page. */}
      {!loading && filtered.length > 0 && (
        <button
          type="button"
          className="home-view-toggle"
          onClick={() => setView(v => (v === 'list' ? 'map' : 'list'))}
        >
          {view === 'list' ? (
            <>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M7.5 3.5 2.5 5.5v11l5-2 5 2 5-2v-11l-5 2-5-2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M7.5 3.5v11M12.5 5.5v11" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
              Map
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
              </svg>
              List
            </>
          )}
        </button>
      )}
    </main>
  );
}
